import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus,
  Hammer,
  Loader2,
  CheckCircle,
  XCircle,
  Download,
  Eye,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const DEFAULT_CHECKLIST = [
  { item: 'Handle Condition', status: 'pass' },
  { item: 'Head/Working End Secure', status: 'pass' },
  { item: 'No Cracks or Damage', status: 'pass' },
  { item: 'No Mushrooming', status: 'pass' },
  { item: 'Proper Guard/Shield', status: 'pass' },
  { item: 'Clean and Free of Debris', status: 'pass' },
  { item: 'Proper Storage Available', status: 'pass' },
  { item: 'Cord/Cable Condition (if powered)', status: 'na' }
];

const ACTION_OPTIONS = [
  { value: 'keep_in_service', label: 'Keep in Service' },
  { value: 'remove_from_service', label: 'Remove from Service' }
];

export default function AdminHandToolInspections() {
  const { authAxios, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [inspections, setInspections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  const [formData, setFormData] = useState({
    tool_name: '',
    tool_id: '',
    project_id: '',
    checklist: [...DEFAULT_CHECKLIST],
    defects_noted: '',
    action_taken: 'keep_in_service',
    signature: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [inspRes, projRes] = await Promise.all([
        authAxios.get('/forms/hand-tool-inspections'),
        authAxios.get('/projects')
      ]);
      setInspections(inspRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      tool_name: '',
      tool_id: '',
      project_id: '',
      checklist: [...DEFAULT_CHECKLIST],
      defects_noted: '',
      action_taken: 'keep_in_service',
      signature: ''
    });
  };

  const updateChecklistItem = (index, status) => {
    const newChecklist = [...formData.checklist];
    newChecklist[index].status = status;
    
    const hasFails = newChecklist.some(item => item.status === 'fail');
    setFormData({
      ...formData,
      checklist: newChecklist,
      action_taken: hasFails ? 'remove_from_service' : 'keep_in_service'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tool_name || !formData.tool_id || !formData.signature) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/forms/hand-tool-inspections', {
        ...formData,
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null
      });
      toast.success('Hand tool inspection submitted');
      if (formData.action_taken === 'remove_from_service') {
        toast.warning('Tool marked as removed from service');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inspection');
    } finally {
      setSubmitting(false);
    }
  };

  const exportPDF = async (inspectionId) => {
    try {
      const response = await authAxios.get(`/pdf/hand-tool-inspection/${inspectionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hand_tool_inspection_${inspectionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const getActionBadge = (action) => {
    if (action === 'keep_in_service') {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />In Service</Badge>;
    }
    return <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-1" />Removed</Badge>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="hand-tool-inspections">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Hand Tool Inspections</h1>
          <p className="text-stone-500 mt-1">Inspect and document hand tool conditions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-tool-inspection-btn">
              <Plus className="w-5 h-5 mr-2" />
              New Inspection
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hammer className="w-5 h-5 text-emerald-600" />
                Hand Tool Inspection Form
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tool Name *</Label>
                  <Input
                    value={formData.tool_name}
                    onChange={(e) => setFormData({ ...formData, tool_name: e.target.value })}
                    placeholder="e.g., Claw Hammer, Screwdriver Set"
                    required
                    data-testid="tool-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tool ID/Serial # *</Label>
                  <Input
                    value={formData.tool_id}
                    onChange={(e) => setFormData({ ...formData, tool_id: e.target.value })}
                    placeholder="e.g., HT-001"
                    required
                    data-testid="tool-id"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Project (Optional)</Label>
                <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Inspection Checklist</Label>
                <div className="border rounded-lg divide-y">
                  {formData.checklist.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3">
                      <span className="font-medium text-sm">{item.item}</span>
                      <div className="flex items-center gap-2">
                        {['pass', 'fail', 'na'].map(status => (
                          <Button
                            key={status}
                            type="button"
                            size="sm"
                            variant={item.status === status ? 'default' : 'outline'}
                            className={item.status === status ? (
                              status === 'pass' ? 'bg-emerald-600' :
                              status === 'fail' ? 'bg-rose-600' : 'bg-stone-600'
                            ) : ''}
                            onClick={() => updateChecklistItem(idx, status)}
                          >
                            {status.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Defects/Damage Noted</Label>
                <Textarea
                  value={formData.defects_noted}
                  onChange={(e) => setFormData({ ...formData, defects_noted: e.target.value })}
                  placeholder="Describe any defects, damage, or wear..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Action Taken *</Label>
                <Select value={formData.action_taken} onValueChange={(v) => setFormData({ ...formData, action_taken: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTIONS.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Signature (Type Name) *</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  placeholder="Type your full name as signature"
                  required
                  data-testid="tool-signature"
                />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Inspection
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Inspections Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Hammer className="w-5 h-5 text-stone-400" />
            Hand Tool Inspections ({inspections.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Hammer className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hand tool inspections found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tool Name</TableHead>
                    <TableHead>Tool ID</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right sticky right-0 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inspections.map((insp) => (
                    <TableRow key={insp.id}>
                      <TableCell className="font-medium">{insp.tool_name}</TableCell>
                      <TableCell className="font-mono text-sm">{insp.tool_id}</TableCell>
                      <TableCell>{insp.inspector_name}</TableCell>
                      <TableCell>{insp.project_name || '-'}</TableCell>
                      <TableCell>{getActionBadge(insp.action_taken)}</TableCell>
                      <TableCell>{fmtTz(insp.created_at, 'MMM d, yyyy h:mm a')}</TableCell>
                      <TableCell className="text-right space-x-1 sticky right-0 bg-white whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSelectedInspection(insp); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => exportPDF(insp.id)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              if (!window.confirm('Delete this hand-tool inspection permanently?')) return;
                              try {
                                await authAxios.delete(`/forms/hand-tool-inspections/${insp.id}`);
                                toast.success('Hand-tool inspection deleted');
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                            }}
                            data-testid={`delete-handtool-${insp.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Inspection Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Hand Tool Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Tool Name</p>
                  <p className="font-medium">{selectedInspection.tool_name}</p>
                </div>
                <div>
                  <p className="text-stone-500">Tool ID</p>
                  <p className="font-medium font-mono">{selectedInspection.tool_id}</p>
                </div>
                <div>
                  <p className="text-stone-500">Inspector</p>
                  <p className="font-medium">{selectedInspection.inspector_name}</p>
                </div>
                <div>
                  <p className="text-stone-500">Action Taken</p>
                  {getActionBadge(selectedInspection.action_taken)}
                </div>
              </div>
              <div>
                <p className="text-stone-500 text-sm mb-2">Checklist</p>
                <div className="border rounded-lg divide-y">
                  {selectedInspection.checklist?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 text-sm">
                      <span>{item.item}</span>
                      <Badge className={
                        item.status === 'pass' ? 'bg-emerald-100 text-emerald-700' :
                        item.status === 'fail' ? 'bg-rose-100 text-rose-700' :
                        'bg-stone-100 text-stone-600'
                      }>
                        {item.status.toUpperCase()}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              {selectedInspection.defects_noted && (
                <div>
                  <p className="text-stone-500 text-sm">Defects Noted</p>
                  <p className="text-sm">{selectedInspection.defects_noted}</p>
                </div>
              )}
              <div className="pt-4 border-t text-sm">
                <p className="text-stone-500">Signature</p>
                <p className="font-medium italic">{selectedInspection.signature}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
